import {
  CircularProgress,
  InputAdornment,
  InputBaseComponentProps,
  TextField,
} from "@mui/material";
import { useTiers } from "api/tiers";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { forwardRef, useEffect, useState } from "react";
import { useStyles } from "./styles";
import { TierDisplayName } from "./TierDisplayName";

export type Tier = {
  levelId: string;
  name: string;
  description?: string;
  active: boolean;
};

export type TierExtended = Tier & {
  tierSet: {
    id: string;
    name: string;
  };
};

interface Props {
  value: string[] | string;
  isOpen: boolean;
  label?: React.ReactNode;
  setIsOpen: (val: boolean) => void;
  disabled?: boolean;
  loadedTiers: TierExtended[];
  dataTestId?: string;
  isLoading: boolean;
  placeholder?: string;
  multiple?: boolean;
  size?: "medium" | "small";
  error?: string;
}

export const Value = ({
  value,
  isOpen,
  label,
  setIsOpen,
  disabled,
  loadedTiers,
  dataTestId,
  isLoading,
  placeholder,
  multiple,
  size = "medium",
  error,
}: Props) => {
  const areTierSetLoaded = loadedTiers.length > 0;
  const [tiersId, setTiersId] = useState<string[]>([]);
  const [tiersData, setTiersData] = useState<TierExtended[]>([]);
  const classes = useStyles({ size });
  const { useTierData } = useTiers();
  const { data: initialData } = useTierData(tiersId);

  useEffect(() => {
    if (!areTierSetLoaded) return;

    const isSingleValue = typeof value === "string";
    if (isSingleValue) {
      const tierData = loadedTiers.find((tier) => tier.levelId === value);
      if (tierData) {
        return setTiersData([tierData]);
      }
      return setTiersId([value]);
    }

    const preFetchedTiers: TierExtended[] = [];
    const tiersToFetch: string[] = [];
    value?.forEach((levelId) => {
      const tierData = loadedTiers.find((tier) => tier.levelId === levelId);
      if (tierData) {
        return preFetchedTiers.push(tierData);
      }
      tiersToFetch.push(levelId);
    });

    setTiersId(tiersToFetch);
    setTiersData(preFetchedTiers);
    // eslint-disable-next-line
  }, [value, setTiersData, setTiersId, areTierSetLoaded]);

  useEffect(() => {
    if (initialData) {
      setTiersData([...tiersData, ...initialData]);

      setTiersId([]);
    }
  }, [initialData, tiersData]);

  const labelValue = tiersData.map((tier: Tier) => {
    return tier.name;
  });
  const shouldShrink = isOpen || value?.length > 0;
  const lengthToCheck = Array.isArray(value) ? value.length : 1;
  const areTiersDataLoading =
    (value && lengthToCheck !== tiersData.length) || isLoading;

  return (
    <div>
      <TextField
        InputLabelProps={{
          shrink: shouldShrink,
        }}
        label={label}
        inputProps={{
          readOnly: true,
          "data-test-id": dataTestId || "tierset-tiers-value",
        }}
        value={labelValue}
        onClick={() => {
          !disabled && setIsOpen(!isOpen);
        }}
        sx={{
          input: {
            width: "100%",
          },
        }}
        disabled={disabled}
        fullWidth
        error={!!error}
        helperText={error && error}
        className={classes.input}
        InputProps={{
          className: classes.inputBase,
          endAdornment: (
            <InputAdornment position="end">
              {areTiersDataLoading && <CircularProgress size={16} />}
              <ChevronDownIcon />
            </InputAdornment>
          ),
          inputComponent: forwardRef<HTMLInputElement, InputBaseComponentProps>(
            (props, ref) => {
              return (
                <div
                  {...props}
                  className={`${props.className} ${classes.wrapper} tierset-dropdown-value`}
                  ref={ref}
                >
                  {tiersData.map((tier, index) => {
                    const isLastItem = index === tiersData.length - 1;

                    return (
                      <>
                        <TierDisplayName
                          key={index}
                          name={tier.name}
                          tierSetName={tier.tierSet.name}
                          multiple={multiple}
                          size={size}
                        />
                        {!isLastItem && (
                          <span className={classes.separator}>,</span>
                        )}
                      </>
                    );
                  })}
                </div>
              );
            },
          ),
        }}
        FormHelperTextProps={{
          className: classes.helperText,
        }}
        placeholder={placeholder}
        size={size}
      />
    </div>
  );
};
